/**
 * 릴레이 준비 경고(SR-MODE-07 부기 2026-09-30 · FS-08-01/02) — 이 프로그램이 <b>리모컨 없이</b> 움직이는가.
 *
 * 릴레이 구간에는 리모컨이 없다. Running(i) 에 룸이 `relayIn` 을 알리면 '🔗 릴레이: 들어오면' 모자 블록이 돌고, 모자 블록이
 * 없는 프로그램은 기계 시작을 '들어옴' 으로 본다. 그런데 대부분의 작품은 리모컨으로 모는 프로그램이다(되풀이 안에서 리모컨이
 * 눌렸을 때만 모터를 돌리고 아니면 멈춤) — 릴레이에서는 아무것도 움직이지 않고 타임아웃(30초)으로 떨어진다.
 *
 * <p>판정은 <b>리모컨을 아무도 만지지 않는다</b>고 두고 프로그램을 훑는다(추상 실행 — 틱 없음):
 * 리모컨 식은 쉬는 값(방향 · 버튼 거짓 · 슬라이더 0), 상수는 그 값, 나머지(센서 · 변수 · 타이머 · 난수 …)는 <b>모름</b>.
 * 조건이 확실히 거짓인 갈래 · 확실히 거짓인 '될 때까지 기다리기' 뒤 · '계속 반복' 뒤 · 리모컨 모자 블록은 닿지 않는다.
 * 닿는 곳에 속도가 확실히 0 이 아닌 모터 블록이 하나라도 있으면 움직인다고 본다.
 *
 * <p><b>확실할 때만 경고한다</b> — '모름' 은 늘 움직이는 쪽으로 기운다(모르는 조건은 두 갈래 다 · 모르는 속도는 움직임).
 * 틀린 경고는 멀쩡한 프로그램을 가진 아이를 Bench 로 보낸다. 놓친 경고는 종전(타임아웃 뒤 다시 준비)과 같다.
 *
 * AST 는 run-bundle `program.ast`(bench-ast@0 — `@picnic/block-runtime` Program)다. 이 셸은 그 패키지에 기대지 않으므로
 * 모양을 방어적으로 읽는다(모르는 모양이면 `unknown` — 경고 없음).
 */

/**
 * - `relay` — '릴레이: 들어오면' 모자 블록이 있다(경고 없음)
 * - `moves` — 리모컨 없이 닿는 모터 블록이 있다(경고 없음)
 * - `remoteOnly` — 모터 블록은 있는데 전부 리모컨이 있어야 닿는다(경고)
 * - `still` — 모터 블록이 없다(경고)
 * - `unknown` — 프로그램이 없거나(정적 전시) 읽을 수 없다(경고 없음)
 */
export type RelayMotion = "relay" | "moves" | "remoteOnly" | "still" | "unknown";

/** 경고할 것인가 — 확실히 스스로 움직이지 않는 두 경우만 */
export const relayWarns = (m: RelayMotion | null | undefined): m is "remoteOnly" | "still" =>
  m === "remoteOnly" || m === "still";

type Node = Record<string, unknown>;
/** 모름 */
const U = Symbol("unknown");
type Val = number | boolean | string | typeof U;

const obj = (v: unknown): Node | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Node) : null;
const list = (v: unknown): Node[] =>
  Array.isArray(v) ? v.map(obj).filter((x): x is Node => x !== null) : [];

const MOTOR_OPS = new Set(["motorRun", "motorRunTime", "motorStart"]);

/** 리모컨이 쉬는 동안의 값 — 확실하지 않으면 {@link U} */
function val(x: unknown): Val {
  const e = obj(x);
  if (!e) return U;
  switch (e.e) {
    case "num":
      return typeof e.value === "number" ? e.value : U;
    case "text":
      return typeof e.value === "string" ? e.value : U;
    case "bool":
      return typeof e.value === "boolean" ? e.value : U;
    case "remoteDpad":
    case "remoteButton":
      return false;
    case "remoteSlider":
      return 0;
    case "not": {
      const v = val(e.value);
      return typeof v === "boolean" ? !v : U;
    }
    case "logic": {
      const a = val(e.left);
      const b = val(e.right);
      if (e.op === "and")
        return a === false || b === false ? false : a === true && b === true ? true : U;
      if (e.op === "or")
        return a === true || b === true ? true : a === false && b === false ? false : U;
      return U;
    }
    case "arith": {
      const a = val(e.left);
      const b = val(e.right);
      // 0 을 곱하면 무엇이든 0 — '슬라이더 × 5' 가 흔한 모양이다
      if (e.op === "*" && (a === 0 || b === 0)) return 0;
      if (typeof a !== "number" || typeof b !== "number") return U;
      switch (e.op) {
        case "+":
          return a + b;
        case "-":
          return a - b;
        case "*":
          return a * b;
        case "/":
          return b === 0 ? U : a / b;
        default:
          return U;
      }
    }
    case "compare": {
      const a = val(e.left);
      const b = val(e.right);
      if (a === U || b === U || typeof a !== typeof b) return U;
      switch (e.op) {
        case "==":
          return a === b;
        case "!=":
          return a !== b;
        case "<":
          return a < b;
        case ">":
          return a > b;
        case "<=":
          return a <= b;
        case ">=":
          return a >= b;
        default:
          return U;
      }
    }
    default:
      return U;
  }
}

/** 확실히 0 인가(모터 속도 · 각도 · 시간) */
const zero = (x: unknown) => val(x) === 0;

class Walk {
  moves = false;
  private readonly seen = new Set<string>();
  constructor(private readonly procs: Map<string, Node[]>) {}

  /** 몸통을 위에서 아래로 — 뒤가 닿지 않게 되면(계속 반복 · 영영 기다림) false */
  ops(ops: Node[]): boolean {
    for (const o of ops) {
      if (this.moves) return false;
      if (!this.op(o)) return false;
    }
    return true;
  }

  /** 한 문 — 다음 문으로 넘어가는가 */
  private op(o: Node): boolean {
    const k = o.op;
    if (typeof k === "string" && MOTOR_OPS.has(k)) {
      const still =
        zero(o.speed) ||
        (k === "motorRun" && zero(o.degrees)) ||
        (k === "motorRunTime" && zero(o.seconds));
      if (!still) this.moves = true;
      return true;
    }
    switch (k) {
      case "if": {
        const c = val(o.cond);
        const a = c !== false && this.ops(list(o.then));
        const b = c !== true && this.ops(list(o.otherwise));
        // 닿는 갈래가 다 뒤가 막히면 그 뒤도 닿지 않는다
        return a || b;
      }
      case "while": {
        const c = val(o.cond);
        if (c === false) return true;
        this.ops(list(o.body));
        return c !== true;
      }
      case "repeat": {
        const n = val(o.times);
        if (typeof n === "number" && n <= 0) return true;
        this.ops(list(o.body));
        return true;
      }
      case "forever":
        this.ops(list(o.body));
        return false;
      case "waitUntil":
        return val(o.cond) !== false;
      case "callBlock": {
        const name = typeof o.name === "string" ? o.name : "";
        if (this.seen.has(name)) return true;
        this.seen.add(name);
        return this.ops(this.procs.get(name) ?? []);
      }
      default:
        return true;
    }
  }
}

/** 어디든 모터 블록이 있는가(닿든 안 닿든) */
function hasMotor(v: unknown): boolean {
  if (Array.isArray(v)) return v.some(hasMotor);
  const o = obj(v);
  if (!o) return false;
  if (typeof o.op === "string" && MOTOR_OPS.has(o.op)) return true;
  return Object.values(o).some(hasMotor);
}

/** run-bundle `program.ast` → 릴레이에서 스스로 움직이는가 */
export function relayMotion(ast: unknown): RelayMotion {
  const p = obj(ast);
  if (!p || !Array.isArray(p.start)) return "unknown";
  const events = list(p.events);
  if (events.some((h) => h.on === "relayIn")) return "relay";
  const procs = new Map<string, Node[]>();
  for (const pr of list(p.procedures))
    if (typeof pr.name === "string") procs.set(pr.name, list(pr.body));
  const w = new Walk(procs);
  w.ops(list(p.start));
  if (w.moves) return "moves";
  return hasMotor(p) ? "remoteOnly" : "still";
}
